import { Injectable } from '@angular/core';
import { CellData } from '@ddt-web/data-service';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import * as intersection from 'lodash/intersection';

@Injectable({
	providedIn: 'root'
})
export class TableFilterService {
	/**
	 * Filters the given data in the provided array (format of [key: string]: CellData).
	 *
	 * @param dataArrayToFilter array to be filtered in the format [key: string]: CellData
	 * @param filters map of key and values, wherein key(string) will contain the path in array to lookup for 
	 * and value is string array which contains all the value to be filtered
	 * @returns the filtered array
	 */
	public filterTableData(
		dataArrayToFilter: Array<{ [key: string]: CellData }>,
		filters: Map<string, Map<string, string[]>>
	): Array<{ [key: string]: CellData }> {
		const filteredResult = dataArrayToFilter.reduce((retValue, fund) => {
			const isFundIncludedInFilter = Array.from(filters.keys()).every(filterCategoryKey => {
				const isMorningstarCategory = !!fund.isMorningstarCategory?.rawValue;
				const match = Array.from(filters.get(filterCategoryKey).keys()).some(key => {
					const columnValue = get(fund, key);

					// check if filter is for blk ideas
					const blkIdeasFilters = ['status.attribute.fundIdea.group', 'status.attribute.fundIdea.isSuggestedAddition'];
					const isBlkIdeaFilter = blkIdeasFilters.includes(key);
					const isBlkIdea = this.isBlkIdea(fund);

					// tags is applied to both 
					if (key !== 'tags.rawValue') {
						// if fund is blk idea and filter is not for blk ideas, ignore it
						// if fund is not blk idea and filter is for blk ideas, ignore it
						if ((!isBlkIdeaFilter && isBlkIdea) || (isBlkIdeaFilter && !isBlkIdea)) {
							return true;
						}
					}

					return Array.isArray(columnValue) ?
						this.filterMatch(filters.get(filterCategoryKey).get(key), columnValue) :
						filters.get(filterCategoryKey).get(key).includes(columnValue || columnValue?.toString());
				});
				return match || isMorningstarCategory;
			});
			if (isFundIncludedInFilter) {
				retValue.push(fund);
			}
			return retValue;
		}, []);

		// remove categories that have blk ideas only after filtering 
		const hasOnlyBlkIdeas = cloneDeep(filteredResult)
			.filter(row => !row.isMorningstarCategory)
			.every(fund => this.isBlkIdea(fund));

		if (filteredResult?.length > 1 && !hasOnlyBlkIdeas) {
			return filteredResult;
		} else {
			return [];
		}
	}

	/**
	 * This function checks if the columnValue is empty (no tags)
	 * if yes, it returns true if the fund isn't a fund idea and the 'noTag' filter is deselected
	 * if not, it checks against other deselected filters and returns true if a match is found
	 * The reason it checks for untagged columnValue first instead of if the 'noTag' filter key is present is because
	 * the true line of the ternary operator is hit if there are other filters besides 'noTag' and so, they do not get filtered out
	 * since the columnValue.length !== 0
	 * 
	 * @param filterKey the tag to be filtered out
	 * @param columnValue list of tags associated with a cell data, is undefined for the data containing the name of the fund group
	 * @returns true if any of the filter key matches the any of column value
	 */
	private filterMatch(filterKey: string[], columnValue: string[]): boolean {
		return columnValue.length === 0 ?
			filterKey?.includes('noTag') :
			intersection(filterKey, columnValue)?.length > 0;
	}

	private isBlkIdea(fund: any): boolean {
		const fundIdea = fund.status?.attribute?.fundIdea;
		return fundIdea?.isFundIdea && fundIdea?.isSuggestedAddition;
	}
}
